// Coach-Kalender auf /coach (W3.5, 29.10.2026, Doku docs/coach-cockpit/w3-kalender.md): Woche · Monat · Liste.
// Inhalt: PT-Termine, Check-ups, Gruppentermine, private Blocker; Kurse als Einsatz des Trainers ausgegraut. Abgesagte
// bleiben durchgestrichen stehen. Klick in freie Zeit legt einen Termin an (Typ wählen). Trainer sehen nur ihre eigenen
// Termine, Overviewer/Master alle mit Trainerfilter — entschieden wird auf dem Server (/api/coach/kalender).
// Raster und Spuren aus dem Einsatz-Kalender des CRM (components/EinsatzKalender.js), nicht neu erfunden.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, ChevronLeft, ChevronRight, Clock, Plus, Search, X } from 'lucide-react';
import AngebotsvorlagenKarte from '@/components/AngebotsvorlagenKarte';
import { iso, kw, minuten, montagVon, plusTage, spuren } from '@/components/EinsatzKalender';

const TAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const TAG_KEYS = ['mo', 'di', 'mi', 'do', 'fr', 'sa', 'so'];
const VON_H = 6, BIS_H = 22, PX_H = 52;
const ART = {
  pt: { label: 'PT', klasse: 'bg-marke/15 border-marke/40 text-foreground' },
  gruppe: { label: 'Gruppe', klasse: 'bg-sky-500/10 border-sky-500/40 text-foreground' },
  checkup: { label: 'Check-up', klasse: 'bg-marke/15 border-marke/40 text-foreground' },
  anfrage: { label: 'Anfrage', klasse: 'bg-amber-500/10 border-amber-500/60 border-dashed text-foreground' },
  blocker: { label: 'Blocker', klasse: 'bg-muted border-border border-dashed text-muted-foreground' },
  kurs: { label: 'Kurse', klasse: 'bg-muted/50 border-border/60 text-muted-foreground opacity-70' },
};
const NEU_TYPEN = [['pt', 'PT'], ['checkup', 'Check-up'], ['gruppe', 'Gruppe'], ['blocker', 'Blocker']];
const DAUER = { pt: 60, checkup: 30, gruppe: 60, blocker: 60 };
const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const datumKurz = (d) => `${TAGE[(new Date(`${d}T12:00:00`).getDay() + 6) % 7]} ${d.slice(8, 10)}.${d.slice(5, 7)}.`;
const knopf = 'inline-flex h-11 items-center justify-center gap-1.5 rounded-[10px] border border-border bg-card px-3 text-sm font-semibold transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';
const knopfMarke = 'inline-flex h-11 items-center justify-center gap-1.5 rounded-[10px] bg-marke px-4 text-sm font-bold text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';
const feld = 'h-11 w-full rounded-[10px] border border-border bg-card px-3 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function meldung(e, rest) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && d.meldung) return d.meldung;
  return rest;
}

function zeitraum(ansicht, bezug) {
  if (ansicht === 'woche') { const m = montagVon(bezug); return [m, plusTage(m, 6)]; }
  if (ansicht === 'monat') { const m = montagVon(new Date(bezug.getFullYear(), bezug.getMonth(), 1)); return [m, plusTage(m, 41)]; }
  const h = new Date(); return [new Date(h.getFullYear(), h.getMonth(), h.getDate()), plusTage(h, 27)];
}

function Chip({ e, onClick, klein }) {
  return (
    <button type="button" onClick={onClick} data-testid={`ck-eintrag-${e.id}`}
      className={`w-full truncate rounded-md border px-1.5 text-left ${klein ? 'py-0.5 text-[10px]' : 'py-1 text-xs'} ${ART[e.art]?.klasse || ''} ${e.abgesagt ? 'line-through opacity-60' : ''}`}>
      {e.erbracht && <Check className="mr-0.5 inline h-3 w-3" aria-label="erbracht" />}
      {e.start} {e.art === 'blocker' ? 'Belegt' : e.art === 'kurs' ? e.titel : `${ART[e.art]?.label}${e.kunde ? ` · ${e.kunde}` : ''}`}
    </button>
  );
}

/* Personensuche (nur ID und Name vom Server; Trainer sehen nur ihre Kunden) */
function PersonSuche({ coach, onWahl, platzhalter = 'Kunde suchen' }) {
  const [q, setQ] = useState('');
  const [treffer, setTreffer] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setTreffer([]); return undefined; }
    const id = setTimeout(() => coach.get('/kalender/personen', { params: { q } }).then((r) => setTreffer(r.data.treffer || [])).catch(() => setTreffer([])), 250);
    return () => clearTimeout(id);
  }, [q, coach]);
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={platzhalter} className={`${feld} pl-9`} data-testid="ck-person-suche" />
      {treffer.length > 0 && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-[10px] border border-border bg-card shadow-lg">
          {treffer.map((t) => (
            <button key={t.id} type="button" className="block h-11 w-full px-3 text-left text-sm hover:bg-muted"
              onClick={() => { onWahl(t); setQ(''); setTreffer([]); }}>{t.name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function NeuerTermin({ coach, vorlage, leitung, trainerListe, trainerId, onFertig, onAbbrechen }) {
  const [typ, setTyp] = useState('pt');
  const [datum, setDatum] = useState(vorlage.datum);
  const [start, setStart] = useState(vorlage.start);
  const [dauer, setDauer] = useState(60);
  const [person, setPerson] = useState(vorlage.person || null);     // vorgewählt aus der Akte (Integration W3)
  const [gruppe, setGruppe] = useState([]);
  const [titel, setTitel] = useState('');
  const [notiz, setNotiz] = useState('');
  const [melden, setMelden] = useState(false);
  const [fuer, setFuer] = useState(trainerId && trainerId !== 'alle' ? trainerId : '');
  const [ausserhalb, setAusserhalb] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => { setDauer(DAUER[typ]); }, [typ]);

  const speichern = async (ausserhalbOk = false) => {
    setLaeuft(true);
    try {
      const body = { typ, datum, start, ende: hhmm(Math.min(minuten(start) + Number(dauer), 23 * 60 + 59)), notiz: notiz || undefined,
        ausserhalb_ok: ausserhalbOk, ...(fuer ? { trainer_id: fuer } : {}) };
      if (typ === 'pt' && person) body.lead_id = person.id;
      if (typ === 'checkup') { body.lead_id = person?.id; body.melden = melden; }
      if (typ === 'gruppe') { body.teilnehmer = gruppe.map((g) => g.id); body.titel = titel || undefined; }
      await coach.post('/kalender/termine', body);
      toast.success(typ === 'blocker' ? 'Blocker eingetragen' : 'Termin eingetragen');
      onFertig();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (leitung && d && typeof d === 'object' && d.code === 'ausserhalb_arbeitszeit') setAusserhalb(d.meldung);
      else toast.error(meldung(e, 'Termin nicht gespeichert'));
    } finally { setLaeuft(false); }
  };
  const brauchtPerson = typ === 'checkup' && !person;
  const gruppeZuKlein = typ === 'gruppe' && gruppe.length < 2;

  return (
    <div className="space-y-3" data-testid="ck-neu">
      <div className="grid grid-cols-4 gap-1 rounded-[12px] border border-border bg-muted/40 p-1" role="radiogroup" aria-label="Art des Termins">
        {NEU_TYPEN.map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={typ === k} onClick={() => setTyp(k)} data-testid={`ck-typ-${k}`}
            className={`h-11 rounded-[10px] text-sm font-semibold ${typ === k ? 'bg-card shadow-sm' : 'text-muted-foreground'}`}>{l}</button>
        ))}
      </div>
      {leitung && (
        <label className="block text-xs font-semibold text-muted-foreground">Trainer
          <select value={fuer} onChange={(e) => setFuer(e.target.value)} className={`${feld} mt-1`} data-testid="ck-neu-trainer">
            <option value="">Ich selbst</option>
            {trainerListe.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
      )}
      <div className="grid grid-cols-3 gap-2">
        <label className="text-xs font-semibold text-muted-foreground">Tag<input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} className={`${feld} mt-1`} /></label>
        <label className="text-xs font-semibold text-muted-foreground">Beginn<input type="time" step="900" value={start} onChange={(e) => setStart(e.target.value)} className={`${feld} mt-1`} /></label>
        <label className="text-xs font-semibold text-muted-foreground">Minuten<input type="number" min="15" max="240" step="15" value={dauer} onChange={(e) => setDauer(e.target.value)} className={`${feld} mt-1`} /></label>
      </div>
      {(typ === 'pt' || typ === 'checkup') && (
        person ? (
          <div className="flex h-11 items-center justify-between rounded-[10px] border border-border px-3 text-sm font-semibold">
            {person.name}<button type="button" onClick={() => setPerson(null)} aria-label="Kunde entfernen" className="p-2"><X className="h-4 w-4" /></button>
          </div>
        ) : <PersonSuche coach={coach} onWahl={setPerson} platzhalter={typ === 'checkup' ? 'Kunde suchen (Pflicht)' : 'Kunde suchen (optional)'} />
      )}
      {typ === 'gruppe' && (
        <>
          <input value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="Titel, z. B. Zweierteam Boxen" className={feld} maxLength={120} />
          <PersonSuche coach={coach} platzhalter="Teilnehmer hinzufügen (2–12)"
            onWahl={(p) => setGruppe((g) => (g.some((x) => x.id === p.id) || g.length >= 12 ? g : [...g, p]))} />
          <div className="flex flex-wrap gap-1.5">
            {gruppe.map((g) => (
              <span key={g.id} className="inline-flex h-9 items-center gap-1 rounded-full border border-border px-3 text-xs font-semibold">
                {g.name}<button type="button" aria-label={`${g.name} entfernen`} onClick={() => setGruppe((x) => x.filter((y) => y.id !== g.id))}><X className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">Die Teilnehmer sehen in der App nur ihren eigenen Termin, nie die anderen.</p>
        </>
      )}
      <input value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder={typ === 'blocker' ? 'Notiz (nur für dich, z. B. Arzt)' : 'Notiz (optional)'} className={feld} maxLength={500} />
      {typ === 'checkup' && (
        <label className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={melden} onChange={(e) => setMelden(e.target.checked)} className="h-5 w-5 accent-marke" />Kunde per Push/Mail benachrichtigen</label>
      )}
      {typ === 'blocker' && <p className="text-[11px] text-muted-foreground">Ein Blocker ist nur Zeit: kein Kunde, im Kalender-Abo und für andere nur „Belegt“.</p>}
      {ausserhalb && (
        <div className="rounded-[10px] border border-amber-500/60 bg-amber-500/10 p-3 text-sm">
          <p>{ausserhalb}</p>
          <button type="button" className={`${knopf} mt-2`} onClick={() => speichern(true)} disabled={laeuft}>Trotzdem eintragen</button>
        </div>
      )}
      <div className="flex gap-2">
        <button type="button" className={knopfMarke} onClick={() => speichern(false)} disabled={laeuft || brauchtPerson || gruppeZuKlein} data-testid="ck-neu-speichern">
          <Plus className="h-4 w-4" />Eintragen
        </button>
        <button type="button" className={knopf} onClick={onAbbrechen}>Abbrechen</button>
      </div>
    </div>
  );
}

function Details({ coach, e, leitung, onGeaendert, onSchliessen }) {
  const [laeuft, setLaeuft] = useState(false);
  const [modus, setModus] = useState(null);           // 'absagen' | 'verlegen'
  const [grund, setGrund] = useState('');
  const [neu, setNeu] = useState({ datum: e.datum, start: e.start || '10:00' });
  const tu = async (fn, ok) => {
    setLaeuft(true);
    try { await fn(); toast.success(ok); onGeaendert(); } catch (err) { toast.error(meldung(err, 'Hat nicht geklappt')); } finally { setLaeuft(false); }
  };
  const offen = ['geplant', 'angefragt'].includes(e.status);
  const istPt = ['pt', 'gruppe'].includes(e.art);
  return (
    <div className="space-y-3" data-testid="ck-details">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className={`text-base font-extrabold ${e.abgesagt ? 'line-through' : ''}`}>{e.art === 'blocker' ? 'Belegt' : e.titel}</p>
          <p className="text-sm text-muted-foreground">{datumKurz(e.datum)} · {e.start}{e.ende ? `–${e.ende}` : ''} Uhr</p>
        </div>
        <button type="button" onClick={onSchliessen} aria-label="Schließen" className="flex h-11 w-11 items-center justify-center rounded-[10px] hover:bg-muted"><X className="h-4 w-4" /></button>
      </div>
      {e.kunde && e.art !== 'gruppe' && <p className="text-sm font-semibold">{e.kunde}</p>}
      {e.art === 'gruppe' && (
        <div className="text-sm"><p className="font-semibold">{e.kunde}</p>
          <ul className="mt-1 space-y-0.5 text-muted-foreground">{(e.teilnehmer || []).map((t) => <li key={t.id}>{t.name}</li>)}</ul></div>
      )}
      {e.trainer_name && leitung && <p className="text-xs text-muted-foreground">Trainer: {e.trainer_name}</p>}
      {e.notiz && <p className="text-xs text-muted-foreground">{e.notiz}</p>}
      {e.unter && <p className="text-xs text-muted-foreground">{e.unter}</p>}
      <p className="text-xs font-semibold">
        {e.abgesagt ? `Status: ${e.status}` : e.erbracht ? 'Erbracht' : e.antwort_noetig ? 'Wartet auf Zusage des Trainers' : e.art === 'kurs' ? 'Kurs (nur Ansicht)' : `Status: ${e.status}`}
        {e.coaching ? ' · P1 Coaching (Kontingent)' : ''}
      </p>

      {istPt && e.antwort_noetig && (
        <div className="flex gap-2">
          <button type="button" className={knopfMarke} disabled={laeuft} onClick={() => tu(() => coach.post(`/kalender/termine/${e.id}/antwort`, { annehmen: true }), 'Angenommen')}>Annehmen</button>
          <button type="button" className={knopf} disabled={laeuft} onClick={() => tu(() => coach.post(`/kalender/termine/${e.id}/antwort`, { annehmen: false }), 'Abgelehnt')}>Ablehnen</button>
        </div>
      )}
      {istPt && offen && !modus && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className={knopfMarke} disabled={laeuft} data-testid="ck-erbracht"
            onClick={() => tu(() => coach.post(`/kalender/termine/${e.id}/erbracht`), 'Als erbracht markiert')}><Check className="h-4 w-4" />Als erbracht markieren</button>
          <button type="button" className={knopf} onClick={() => setModus('verlegen')}>Verlegen</button>
          <button type="button" className={knopf} onClick={() => setModus('absagen')}>Absagen</button>
        </div>
      )}
      {e.art === 'blocker' && !e.abgesagt && !modus && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className={knopf} onClick={() => setModus('verlegen')}>Verschieben</button>
          <button type="button" className={knopf} disabled={laeuft} onClick={() => tu(() => coach.delete(`/kalender/termine/${e.id}`), 'Blocker entfernt')}>Blocker entfernen</button>
        </div>
      )}
      {modus === 'absagen' && (
        <div className="space-y-2">
          <input value={grund} onChange={(x) => setGrund(x.target.value)} placeholder="Grund (optional)" className={feld} maxLength={300} />
          <div className="flex gap-2">
            <button type="button" className={knopfMarke} disabled={laeuft} onClick={() => tu(() => coach.post(`/kalender/termine/${e.id}/absagen`, { grund }), 'Abgesagt')}>Absagen</button>
            <button type="button" className={knopf} onClick={() => setModus(null)}>Zurück</button>
          </div>
          {e.coaching && <p className="text-[11px] text-muted-foreground">P1 Coaching: Eine Absage später als 24 Stunden vorher zählt als wahrgenommen.</p>}
        </div>
      )}
      {modus === 'verlegen' && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input type="date" value={neu.datum} onChange={(x) => setNeu((n) => ({ ...n, datum: x.target.value }))} className={feld} />
            <input type="time" step="900" value={neu.start} onChange={(x) => setNeu((n) => ({ ...n, start: x.target.value }))} className={feld} />
          </div>
          <div className="flex gap-2">
            <button type="button" className={knopfMarke} disabled={laeuft}
              onClick={() => tu(() => coach.patch(`/kalender/termine/${e.id}`, { datum: neu.datum, start: neu.start, ende: hhmm(minuten(neu.start) + (minuten(e.ende) - minuten(e.start))) }), 'Verlegt')}>Verlegen</button>
            <button type="button" className={knopf} onClick={() => setModus(null)}>Zurück</button>
          </div>
        </div>
      )}
      {e.art === 'checkup' && e.uebernehmen && (
        <button type="button" className={knopfMarke} disabled={laeuft} onClick={() => tu(() => coach.post(`/kalender/checkups/${e.id}/uebernehmen`), 'Check-up übernommen')}>Check-up übernehmen</button>
      )}
      {e.art === 'anfrage' && <p className="text-xs text-muted-foreground">Check-up-Anfrage ({e.zeitfenster_text || 'Wunschtag'}) — freigeben im CRM-Kalender.</p>}
    </div>
  );
}

/* Check-up-Zeiten für die Online-Buchung (Leistung Check-up am Trainer-Link). Arbeitszeit nur lesen. */
function Verfuegbarkeit({ coach, trainerId, offen }) {
  const [daten, setDaten] = useState(null);
  const [text, setText] = useState({});
  const laden = useCallback(() => coach.get('/kalender/verfuegbarkeit', { params: trainerId && trainerId !== 'alle' ? { trainer_id: trainerId } : {} })
    .then((r) => {
      setDaten(r.data);
      const l = r.data.links?.[0];
      setText(Object.fromEntries(TAG_KEYS.map((k) => [k, ((l?.checkup_fenster || {})[k] || []).map(([a, b]) => `${a}-${b}`).join(', ')])));
    }).catch(() => setDaten({ links: [] })), [coach, trainerId]);
  useEffect(() => { laden(); }, [laden]);

  if (!daten) return null;
  const link = daten.links?.[0];
  if (!link) return <p className="text-sm text-muted-foreground">Kein Trainer-Link verknüpft — Check-up-Zeiten pflegt dann das Studio.</p>;
  const speichern = async () => {
    try {
      const fenster = {};
      TAG_KEYS.forEach((k) => {
        const bl = (text[k] || '').split(',').map((x) => x.trim()).filter(Boolean).map((x) => x.split(/[-–]/).map((y) => y.trim()));
        if (bl.length) fenster[k] = bl;
      });
      await coach.put('/kalender/verfuegbarkeit', { link_id: link.id, checkup_fenster: fenster, ...(trainerId && trainerId !== 'alle' ? { trainer_id: trainerId } : {}) });
      toast.success('Check-up-Zeiten gespeichert');
      laden();
    } catch (e) { toast.error(meldung(e, 'Nicht gespeichert')); }
  };
  const zusammen = TAG_KEYS.filter((k) => text[k]).map((k) => `${TAGE[TAG_KEYS.indexOf(k)]} ${text[k]}`).join(' · ');
  return (
    <div className="space-y-2" data-testid="ck-verfuegbarkeit">
      <p className="text-sm text-muted-foreground" data-testid="ck-fenster-zusammen">{zusammen || 'Keine Fenster eingetragen'}</p>
      {offen && <>
      <p className="text-xs text-muted-foreground">
        Zeiten, in denen Kunden online einen Check-up buchen können{daten.selbstbuchung_an ? '' : ' (Online-Buchung ist zurzeit ausgeschaltet)'}. Format: 10:00-12:00, 16:00-18:00.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {TAG_KEYS.map((k, i) => (
          <label key={k} className="flex items-center gap-2 text-sm">
            <span className="w-8 font-semibold">{TAGE[i]}</span>
            <input value={text[k] || ''} onChange={(e) => setText((t) => ({ ...t, [k]: e.target.value }))} className={feld} placeholder="frei" />
            <span className="hidden w-28 shrink-0 text-[11px] text-muted-foreground lg:block" title="Arbeitszeit">
              {((link.arbeitszeiten || {})[k] || []).map(([a, b]) => `${a}–${b}`).join(', ') || '—'}
            </span>
          </label>
        ))}
      </div>
      <button type="button" className={knopfMarke} onClick={speichern}>Check-up-Zeiten speichern</button>
      </>}
    </div>
  );
}

/* `kunde` (Integration W3): Akte › „Termin“ öffnet den Kalender mit `?ansicht=kalender&kunde=<lead>` — der Kalender lädt
   Name und Sicht über `/kalender/personen/{id}` und öffnet „Termin anlegen“ für diesen Kunden. Danach fällt der Parameter weg. */
export default function CoachKalender({ coach, ich, kunde, gehe }) {
  const [ansicht, setAnsicht] = useState(() => { try { return localStorage.getItem('p1_coach_kalender') || 'woche'; } catch { return 'woche'; } });
  const [bezug, setBezug] = useState(() => new Date());
  const [trainerId, setTrainerId] = useState('');
  const [trainerListe, setTrainerListe] = useState([]);
  const vorlagenLaden = useCallback(() => coach.get('/angebotsvorlagen'), [coach]);
  const [daten, setDaten] = useState(null);
  const [auswahl, setAuswahl] = useState(null);
  const [zeitenOffen, setZeitenOffen] = useState(false);
  const nr = useRef(0);
  const [von, bis] = useMemo(() => zeitraum(ansicht, bezug), [ansicht, bezug]);
  const leitung = !!daten?.leitung;

  useEffect(() => { try { localStorage.setItem('p1_coach_kalender', ansicht); } catch { /* privat */ } }, [ansicht]);
  useEffect(() => { coach.get('/kalender/trainer').then((r) => setTrainerListe(r.data.trainer || [])).catch(() => setTrainerListe([])); }, [coach]);

  const laden = useCallback(async () => {
    const anfrage = ++nr.current;
    try {
      const r = await coach.get('/kalender', { params: { von: iso(von), bis: iso(bis), ...(trainerId ? { trainer_id: trainerId } : {}) } });
      if (anfrage === nr.current) setDaten(r.data);
    } catch (e) { if (anfrage === nr.current) toast.error(meldung(e, 'Kalender nicht geladen')); }
  }, [coach, von, bis, trainerId]);
  useEffect(() => { laden(); }, [laden]);

  useEffect(() => {
    if (!kunde) return undefined;
    let weg = false;
    coach.get(`/kalender/personen/${kunde}`)
      .then((r) => { if (!weg) setAuswahl({ neu: true, datum: iso(new Date()), start: '10:00', person: r.data, ausAkte: kunde }); })
      .catch((e) => { if (!weg) toast.error(meldung(e, 'Kunde nicht gefunden')); });
    return () => { weg = true; };
  }, [coach, kunde]);
  const parameterWeg = () => { if (kunde && gehe) gehe({ ansicht: 'kalender' }); };

  const eintraege = useMemo(() => daten?.eintraege || [], [daten]);
  const heute = iso(new Date());
  // vergangene PT/Gruppen-Termine, die noch nicht als erbracht markiert sind (Mockup: „… ist vorbei“)
  const vorbei = useMemo(() => {
    const jetzt = new Date();
    const h = iso(jetzt), m = jetzt.getHours() * 60 + jetzt.getMinutes();
    return eintraege.filter((e) => ['pt', 'gruppe'].includes(e.art) && e.status === 'geplant' && !e.abgesagt && !e.erbracht && e.start
      && (e.datum < h || (e.datum === h && minuten(e.ende || e.start) <= m))).sort((a, b) => `${a.datum}${a.start}`.localeCompare(`${b.datum}${b.start}`));
  }, [eintraege]);
  const blaettern = (n) => {
    setAuswahl(null);
    setBezug((b) => (ansicht === 'monat' ? new Date(b.getFullYear(), b.getMonth() + n, 1) : plusTage(b, 7 * n)));
  };
  const geaendert = () => { setAuswahl(null); laden(); };
  const erbrachtMarkieren = async (id) => {
    try { await coach.post(`/kalender/termine/${id}/erbracht`); toast.success('Als erbracht markiert'); laden(); }
    catch (err) { toast.error(meldung(err, 'Hat nicht geklappt')); }
  };

  const tage = useMemo(() => Array.from({ length: 7 }, (_, i) => plusTage(von, i)), [von]);
  const proTag = useMemo(() => tage.map((t) => spuren(eintraege.filter((e) => e.datum === iso(t) && e.start)
    .map((e) => ({ ...e, von: minuten(e.start), bis: Math.max(minuten(e.ende || e.start), minuten(e.start) + 20) })))), [tage, eintraege]);

  const freiGeklickt = (tag, ev) => {
    const y = ev.clientY - ev.currentTarget.getBoundingClientRect().top;
    const m = Math.min(BIS_H * 60 - 30, Math.max(VON_H * 60, Math.round((VON_H * 60 + (y / PX_H) * 60) / 15) * 15));
    setAuswahl({ neu: true, datum: iso(tag), start: hhmm(m) });
  };

  const chip = 'inline-flex h-9 items-center rounded-full border px-[11px] text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  const kopf = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Ansicht">
        {[['woche', 'Woche'], ['monat', 'Monat'], ['liste', 'Liste']].map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={ansicht === k} onClick={() => { setAnsicht(k); setAuswahl(null); }} data-testid={`ck-ansicht-${k}`}
            className={`${chip} ${ansicht === k ? 'border-marke bg-marke text-[#140c08]' : 'border-border bg-muted text-foreground hover:bg-muted/70'}`}>{l}</button>
        ))}
      </div>
      {trainerListe.length > 1 && (
        <select value={trainerId} onChange={(e) => { setTrainerId(e.target.value); setAuswahl(null); }} className="ml-auto h-11 rounded-[10px] border border-border bg-card px-3 text-sm font-semibold" data-testid="ck-trainer">
          <option value="">Meine Termine</option>
          <option value="alle">Alle Trainer</option>
          {trainerListe.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      )}
      <button type="button" className={`${knopfMarke} ${trainerListe.length > 1 ? '' : 'ml-auto'}`} onClick={() => setAuswahl({ neu: true, datum: heute < iso(von) ? iso(von) : heute, start: '10:00' })} data-testid="ck-neu-knopf">
        <Plus className="h-4 w-4" />Termin
      </button>
    </div>
  );

  const kartenKopf = (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <div className="flex items-center rounded-[10px] border border-border bg-muted">
        <button type="button" onClick={() => blaettern(-1)} aria-label="Zurück" className="flex h-11 w-11 items-center justify-center"><ChevronLeft className="h-4 w-4" /></button>
        <h2 className="min-w-[150px] px-1 text-center text-base font-bold">
          {ansicht === 'woche' ? `KW ${kw(von)} · ${String(von.getDate()).padStart(2, '0')}.${String(von.getMonth() + 1).padStart(2, '0')}.–${String(bis.getDate()).padStart(2, '0')}.${String(bis.getMonth() + 1).padStart(2, '0')}.`
            : ansicht === 'monat' ? bezug.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }) : 'Nächste 4 Wochen'}
        </h2>
        <button type="button" onClick={() => blaettern(1)} aria-label="Weiter" className="flex h-11 w-11 items-center justify-center"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <button type="button" className={knopf} onClick={() => { setBezug(new Date()); setAuswahl(null); }}>Heute</button>
      <span className="ml-auto text-sm font-semibold text-muted-foreground">Klick in freie Zeit legt Termin an</span>
    </div>
  );

  const woche = (
    <div className="overflow-hidden rounded-[10px] border border-border/60">
      <div className="grid border-b border-border" style={{ gridTemplateColumns: '44px repeat(7, minmax(0, 1fr))' }}>
        <div />
        {tage.map((t, i) => (
          <div key={i} className={`py-2 text-center text-xs ${iso(t) === heute ? 'font-extrabold text-marke' : 'font-semibold text-muted-foreground'}`}>{TAGE[i]} {t.getDate()}.{t.getMonth() + 1}.</div>
        ))}
      </div>
      <div className="max-h-[70vh] overflow-auto">
        <div className="grid min-w-[640px]" style={{ gridTemplateColumns: '44px repeat(7, minmax(0, 1fr))' }}>
          <div className="relative" style={{ height: (BIS_H - VON_H) * PX_H }}>
            {Array.from({ length: BIS_H - VON_H }).map((_, h) => (
              <span key={h} className="absolute right-1 -translate-y-1/2 text-[10px] text-muted-foreground" style={{ top: h * PX_H }}>{h ? `${VON_H + h}:00` : ''}</span>
            ))}
          </div>
          {tage.map((tag, i) => (
            <div key={i} className="relative cursor-cell border-l border-border/40" style={{ height: (BIS_H - VON_H) * PX_H }}
              onClick={(ev) => freiGeklickt(tag, ev)} title="Klicken: Termin eintragen" data-testid={`ck-tag-${iso(tag)}`}>
              {Array.from({ length: BIS_H - VON_H }).map((_, h) => <div key={h} className="absolute left-0 right-0 border-t border-border/30" style={{ top: h * PX_H }} />)}
              {proTag[i].map((e) => {
                const top = Math.max(0, ((e.von - VON_H * 60) / 60) * PX_H);
                const hoehe = Math.max(22, ((Math.min(e.bis, BIS_H * 60) - Math.max(e.von, VON_H * 60)) / 60) * PX_H - 2);
                const breite = 100 / e.spuren;
                return (
                  <button key={e.id} type="button" onClick={(ev) => { ev.stopPropagation(); setAuswahl(e); }} data-testid={`ck-eintrag-${e.id}`}
                    className={`absolute z-10 overflow-hidden rounded-md border px-1.5 py-1 text-left ${ART[e.art]?.klasse || ''} ${e.abgesagt ? 'line-through opacity-60' : ''} ${e.art === 'blocker' ? 'bg-[repeating-linear-gradient(135deg,transparent,transparent_6px,hsl(var(--border)/0.6)_6px,hsl(var(--border)/0.6)_8px)]' : ''} ${auswahl?.id === e.id ? 'ring-2 ring-marke' : ''}`}
                    style={{ top, height: hoehe, left: `calc(${e.spur * breite}% + 2px)`, width: `calc(${breite}% - 4px)` }}>
                    <p className="truncate text-[11px] font-bold leading-tight">
                      {e.erbracht && <Check className="mr-0.5 inline h-3 w-3" aria-label="erbracht" />}
                      {e.art === 'blocker' ? 'Belegt' : e.art === 'kurs' ? e.titel : `${ART[e.art]?.label}${e.kunde ? ` · ${e.kunde}` : ''}`}
                    </p>
                    {hoehe > 32 && <p className="truncate text-[10px] leading-tight opacity-80">{e.start}{e.ende ? `–${e.ende}` : ''}{e.antwort_noetig ? ' · wartet' : ''}{leitung && e.trainer_name && !trainerId ? ` · ${e.trainer_name}` : ''}</p>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const monat = (
    <div className="overflow-hidden rounded-[10px] border border-border/60">
      <div className="grid grid-cols-7 border-b border-border">{TAGE.map((t) => <div key={t} className="py-2 text-center text-xs font-semibold text-muted-foreground">{t}</div>)}</div>
      <div className="grid grid-cols-7">
        {Array.from({ length: 42 }, (_, i) => plusTage(von, i)).map((tag) => {
          const d = iso(tag);
          const liste = eintraege.filter((e) => e.datum === d);
          const fremd = tag.getMonth() !== bezug.getMonth();
          return (
            <div key={d} className={`min-h-[96px] border-b border-l border-border/40 p-1 ${fremd ? 'opacity-50' : ''}`}>
              <button type="button" onClick={() => { setBezug(tag); setAnsicht('woche'); }} className={`mb-1 flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${d === heute ? 'bg-marke text-white' : 'hover:bg-muted'}`}>{tag.getDate()}</button>
              <div className="space-y-0.5">
                {liste.slice(0, 3).map((e) => <Chip key={e.id} e={e} klein onClick={() => setAuswahl(e)} />)}
                {liste.length > 3 && <p className="px-1 text-[10px] text-muted-foreground">+{liste.length - 3} weitere</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const nachTag = useMemo(() => {
    const m = new Map();
    eintraege.forEach((e) => { if (!m.has(e.datum)) m.set(e.datum, []); m.get(e.datum).push(e); });
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [eintraege]);
  const liste = (
    <div className="space-y-4">
      {nachTag.length === 0 && <p className="rounded-[10px] border border-border/60 p-6 text-sm text-muted-foreground">Keine Termine in den nächsten vier Wochen.</p>}
      {nachTag.map(([d, l]) => (
        <section key={d} className="rounded-[10px] border border-border/60 p-3">
          <h3 className={`mb-2 text-sm font-extrabold ${d === heute ? 'text-marke' : ''}`}>{datumKurz(d)}</h3>
          <ul className="divide-y divide-border/50">
            {l.map((e) => (
              <li key={e.id}>
                <button type="button" onClick={() => setAuswahl(e)} className={`flex min-h-11 w-full items-center gap-3 py-2 text-left text-sm ${e.abgesagt ? 'line-through opacity-60' : ''} ${e.art === 'kurs' ? 'opacity-60' : ''}`}>
                  <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span className="w-24 shrink-0 tabular-nums">{e.start}{e.ende ? `–${e.ende}` : ''}</span>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${ART[e.art]?.klasse || ''}`}>{ART[e.art]?.label}</span>
                  <span className="min-w-0 flex-1 truncate font-semibold">{e.art === 'blocker' ? 'Belegt' : e.art === 'kurs' ? e.titel : (e.kunde || e.titel)}</span>
                  {e.erbracht && <Check className="h-4 w-4 text-emerald-500" aria-label="erbracht" />}
                  {leitung && !trainerId && e.trainer_name && <span className="hidden text-xs text-muted-foreground sm:inline">{e.trainer_name}</span>}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );

  const karte = 'rounded-[14px] border border-border bg-card px-5 py-[18px]';
  return (
    <div className="space-y-4" data-testid="coach-kalender">
      {kopf}
      {daten?.hinweis && <p className="coach-hinweis coach-hinweis-text rounded-[12px] border p-3 text-sm">{daten.hinweis}</p>}
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
        <section className={`${karte} min-w-0 flex-1`}>
          {kartenKopf}
          {ansicht === 'woche' ? woche : ansicht === 'monat' ? monat : liste}
          {vorbei.length > 0 && (
            <ul className="mt-2" data-testid="ck-vorbei">
              {vorbei.slice(0, 3).map((e) => (
                <li key={e.id} className="flex flex-wrap items-center justify-between gap-3 border-t border-border py-2.5 text-sm">
                  <span>{ART[e.art]?.label}{e.kunde ? ` · ${e.kunde}` : ''} · {datumKurz(e.datum)} {e.start} ist vorbei</span>
                  <button type="button" className={knopf} onClick={() => erbrachtMarkieren(e.id)} data-testid={`ck-vorbei-erbracht-${e.id}`}><Check className="h-4 w-4" />Als erbracht markieren</button>
                </li>
              ))}
              {vorbei.length > 3 && <li className="border-t border-border pt-2 text-xs text-muted-foreground">+{vorbei.length - 3} weitere vergangene Termine</li>}
            </ul>
          )}
        </section>
        <aside className="xl:w-[340px]">
          <div className="sticky top-4 space-y-3 rounded-[14px] border border-border bg-card px-5 py-[18px]">
            {auswahl?.neu ? (
              <>
                {auswahl.ausAkte && gehe && (
                  <button type="button" className="flex min-h-11 items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"
                    onClick={() => gehe({ ansicht: 'kunde', kunde: auswahl.ausAkte })} data-testid="ck-zur-akte"><ChevronLeft className="h-4 w-4" />Zur Kundenakte</button>
                )}
                <NeuerTermin key={`${auswahl.datum}-${auswahl.start}-${auswahl.person?.id || ''}`} coach={coach} vorlage={auswahl} leitung={leitung} trainerListe={trainerListe}
                  trainerId={trainerId} onFertig={() => { geaendert(); parameterWeg(); }} onAbbrechen={() => { setAuswahl(null); parameterWeg(); }} />
              </>
            ) : auswahl ? (
              <Details key={auswahl.id} coach={coach} e={auswahl} leitung={leitung} onGeaendert={geaendert} onSchliessen={() => setAuswahl(null)} />
            ) : (
              <>
                <p className="text-sm font-extrabold">Diese Ansicht</p>
                <p className="text-sm text-muted-foreground">
                  {eintraege.filter((e) => ['pt', 'gruppe'].includes(e.art) && !e.abgesagt).length} PT/Gruppe ·{' '}
                  {eintraege.filter((e) => e.art === 'checkup').length} Check-ups ·{' '}
                  {eintraege.filter((e) => ['pt', 'gruppe'].includes(e.art) && e.erbracht).length} erbracht
                </p>
                <p className="text-xs text-muted-foreground">Klick in eine freie Zeit der Woche: Termin eintragen. Kurse sind ausgegraut, sie blockieren nichts.</p>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {Object.entries(ART).map(([k, a]) => <span key={k} className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${a.klasse}`}>{a.label}</span>)}
                </div>
              </>
            )}
          </div>
        </aside>
      </div>

      <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
        {ich?.master && <div className="min-w-0 flex-[3]"><AngebotsvorlagenKarte laden={vorlagenLaden} /></div>}
        <section className={`${karte} min-w-0 flex-[2]`} data-testid="ck-zeiten-karte">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h2 className="text-base font-bold">Check-up-Zeiten</h2>
            <button type="button" onClick={() => setZeitenOffen((v) => !v)} className={knopf} aria-expanded={zeitenOffen}>
              {zeitenOffen ? 'Schließen' : 'Bearbeiten'}<ChevronRight className={`h-4 w-4 transition-transform ${zeitenOffen ? 'rotate-90' : ''}`} />
            </button>
          </div>
          <p className="mb-2 text-sm text-muted-foreground">Verfügbarkeitsfenster für Selbstbuchung</p>
          <Verfuegbarkeit coach={coach} trainerId={trainerId} offen={zeitenOffen} />
        </section>
      </div>
    </div>
  );
}
